/**
 * Membership and certification derivation for PGA Coach surfaces.
 *
 * Flag-neutral: directory cards, event hosts, and the coach info panel
 * import this module while `pga-com-coach-profile-ux` is off. Do not
 * gate these helpers on FLAG_COACH_PROFILE_UX. Profile-only UI that
 * consumes them (`CoachAccreditationsRow`, hero crest) stays behind
 * that flag in the component that renders it.
 */

/** Member type codes that are not shown as “PGA Member” (aligns with CoachLogo exclusions). */
const NON_PGA_MEMBER_MEMBER_TYPES = new Set(['AP', 'ST', 'QE'])

/**
 * @param {string | null | undefined} memberType
 * @returns {boolean}
 */
export function coachQualifiesForPgaMemberTag(memberType) {
  return Boolean(memberType && !NON_PGA_MEMBER_MEMBER_TYPES.has(memberType))
}

/**
 * Apprentice / Associate code on PGA membership records.
 *
 * @param {string | null | undefined} memberType
 * @returns {boolean}
 */
export function coachIsAssociate(memberType) {
  return memberType === 'AP'
}

/**
 * Student membership code.
 *
 * @param {string | null | undefined} memberType
 * @returns {boolean}
 */
export function coachIsStudent(memberType) {
  return memberType === 'ST'
}

/**
 * Master Professional is encoded as `certificationLevel === 'MASTER'`
 * (see `CoachLogo` art selection). Mutually exclusive with other
 * `certificationLevel` values, so a coach is at most one of
 * Member-with-cert / Master / ADM via this field.
 *
 * @param {string | null | undefined} certificationLevel
 * @returns {boolean}
 */
export function coachIsMasterProfessional(certificationLevel) {
  if (certificationLevel == null || certificationLevel === '') return false
  return String(certificationLevel).toUpperCase() === 'MASTER'
}

/**
 * ADM certification is sourced from Coach Tools `CoachProfile.admCertified`
 * (membership ADM flag; `false` when the coach has no membership). Legacy
 * story fixtures may still use `certificationLevel === 'ADM'`; either signal
 * qualifies. Do not use `coach.apsTraining` — APS is separate compliance
 * training, not ADM certification.
 *
 * @param {{
 *   certificationLevel?: string | null,
 *   admCertified?: boolean,
 * }} input
 * @returns {boolean}
 */
export function coachIsAdmCertified({ certificationLevel, admCertified } = {}) {
  if (admCertified === true) return true
  if (certificationLevel == null || certificationLevel === '') return false
  return String(certificationLevel).toUpperCase() === 'ADM'
}

/**
 * PGA HOPE certification is sourced from Coach Tools `coach.pgaHopeCoach`
 * and/or upcoming registrable HOPE programs in Algolia
 * (`hasPgaHopeProgram` from `fetchCoachProgramCredentialFlags`).
 *
 * @param {{
 *   pgaHopeCoach?: boolean,
 *   hasPgaHopeProgram?: boolean,
 * }} input
 * @returns {boolean}
 */
export function coachIsPgaHopeCertified({
  pgaHopeCoach,
  hasPgaHopeProgram = false,
} = {}) {
  return pgaHopeCoach === true || hasPgaHopeProgram === true
}

/**
 * Ordered credential labels for the coach profile bio.
 *
 * @param {{
 *   memberType?: string | null,
 *   certificationLevel?: string | null,
 *   admCertified?: boolean,
 *   hasJrLeagueProgram?: boolean,
 *   hasPgaHopeProgram?: boolean,
 * }} input
 * @returns {string[]}
 */
export function buildCoachCredentialTagLabels({
  memberType,
  certificationLevel,
  admCertified,
  hasJrLeagueProgram = false,
  hasPgaHopeProgram = false,
}) {
  const tags = []
  if (coachQualifiesForPgaMemberTag(memberType)) {
    tags.push('PGA Member')
  }
  if (coachIsAdmCertified({ certificationLevel, admCertified })) {
    tags.push('ADM Certified')
  }
  if (hasJrLeagueProgram) {
    tags.push('PGA Jr. League Coach')
  }
  if (hasPgaHopeProgram) {
    tags.push('PGA HOPE Coach')
  }
  return tags
}

/**
 * Accreditation card data for `CoachAccreditationsRow`. Each entry is
 * a separate card in the band; ordering here drives card order on the
 * page. Membership classification is mutually exclusive (a coach is
 * exactly one of Member / Associate / Student, or none of them when
 * `memberType` is missing or holds a code we do not surface, e.g. `QE`).
 *
 * `logoSrc` is intentionally optional. Branded artwork only ships for
 * the two accreditations whose SVGs already live in this repo
 * (`pga-member-logo.svg`, `pga-master-logo.svg`); the rest render as
 * label-only cards until product supplies the missing badges. When
 * adding a new SVG at `public/images/accreditations/<slug>.svg`, set
 * the corresponding `logoSrc` here so the card picks it up — no
 * component change required.
 *
 * @param {{
 *   memberType?: string | null,
 *   certificationLevel?: string | null,
 *   admCertified?: boolean,
 *   pgaHopeCoach?: boolean,
 *   hasPgaHopeProgram?: boolean,
 * }} input
 * @returns {Array<{ id: string, label: string, logoSrc?: string, alt: string }>}
 */
export function buildCoachAccreditations({
  memberType,
  certificationLevel,
  admCertified,
  pgaHopeCoach,
  hasPgaHopeProgram = false,
}) {
  const cards = []

  if (coachQualifiesForPgaMemberTag(memberType)) {
    cards.push({
      id: 'pga-member',
      label: 'PGA of America Member',
      // Uses the dedicated Member crest (PGA of America mark with the
      // "MEMBER" banner) rather than the legacy `/images/pga-member-logo.svg`
      // — the legacy SVG is still consumed by `CoachLogo` in the hero
      // and by the Associate card below (where the "MEMBER" banner
      // would be inaccurate), so the accreditation Member card gets
      // its own asset to avoid coupling those surfaces.
      logoSrc: '/images/accreditations/pga-member.png',
      alt: 'PGA of America Member',
    })
  } else if (coachIsAssociate(memberType)) {
    cards.push({
      id: 'pga-associate',
      label: 'PGA Associate',
      // Label-only: the legacy member crest SVG includes a "MEMBER"
      // banner and must not represent Associates on profile surfaces.
      alt: 'PGA Associate',
    })
  } else if (coachIsStudent(memberType)) {
    cards.push({
      id: 'pga-student',
      label: 'PGA of America Student',
      // TODO(coach-accreditations): drop branded SVG at
      //   public/images/accreditations/pga-student.svg
      // and set logoSrc here.
      alt: 'PGA of America Student',
    })
  }

  if (coachIsMasterProfessional(certificationLevel)) {
    cards.push({
      id: 'master-professional',
      label: 'PGA of America Master Professional',
      // Uses the dedicated Master Professional crest (5-star PGA of
      // America mark) rather than the legacy `/images/pga-master-logo.svg`
      // — that legacy SVG is still consumed by `CoachLogo` for the hero
      // crest, so the accreditation card has its own asset to avoid
      // coupling the two surfaces.
      logoSrc: '/images/accreditations/pga-master.png',
      alt: 'PGA of America Master Professional',
    })
  }

  if (coachIsPgaHopeCertified({ pgaHopeCoach, hasPgaHopeProgram })) {
    cards.push({
      id: 'pga-hope',
      label: 'PGA HOPE Certified',
      logoSrc: '/images/accreditations/pga-hope.png',
      alt: 'PGA HOPE Certified',
    })
  }

  if (coachIsAdmCertified({ certificationLevel, admCertified })) {
    cards.push({
      id: 'adm-certified',
      label: 'ADM Certified',
      logoSrc: '/images/accreditations/adm-certified.png',
      alt: 'ADM Certified',
    })
  }

  return cards
}

/**
 * Compact directory-card credential copy. Keep these short so the card
 * does not repeat "PGA of America" next to facility names that already
 * include the organization. Profile accreditation rows keep the full
 * `buildCoachAccreditations` labels.
 */
const DIRECTORY_MEMBERSHIP_LABEL_OVERRIDES = {
  'pga-member': 'PGA Member',
  'pga-associate': 'PGA Associate',
  'pga-student': 'PGA Student',
  'master-professional': 'Master Professional',
}

const withDirectoryMembershipDisplay = (card) => {
  if (!card) return null
  const label = DIRECTORY_MEMBERSHIP_LABEL_OVERRIDES[card.id]
  if (!label) return card
  return {
    ...card,
    label,
  }
}

/**
 * Single membership accreditation for compact directory cards. Master
 * Professional takes precedence over Member when both apply. Cards show
 * the short directory label only — not a crest.
 *
 * @param {{
 *   memberType?: string | null,
 *   certificationLevel?: string | null,
 *   admCertified?: boolean,
 *   pgaHopeCoach?: boolean,
 *   hasPgaHopeProgram?: boolean,
 * }} input
 * @returns {{ id: string, label: string, logoSrc?: string, alt: string } | null}
 */
export function getCoachMembershipDisplayAccreditation({
  memberType,
  certificationLevel,
  admCertified,
  pgaHopeCoach,
  hasPgaHopeProgram = false,
} = {}) {
  const cards = buildCoachAccreditations({
    memberType,
    certificationLevel,
    admCertified,
    pgaHopeCoach,
    hasPgaHopeProgram,
  })

  if (coachIsMasterProfessional(certificationLevel)) {
    const master = cards.find((card) => card.id === 'master-professional')
    if (master) return withDirectoryMembershipDisplay(master)
  }
  if (coachIsAssociate(memberType)) {
    return withDirectoryMembershipDisplay(cards.find((card) => card.id === 'pga-associate') || null)
  }
  if (coachIsStudent(memberType)) {
    return withDirectoryMembershipDisplay(cards.find((card) => card.id === 'pga-student') || null)
  }
  if (coachQualifiesForPgaMemberTag(memberType)) {
    return withDirectoryMembershipDisplay(cards.find((card) => card.id === 'pga-member') || null)
  }
  return null
}
